'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

const upcomingWorlds = [
  { id: 'world-2', icon: '🧪', name: 'The Lab Files', genre: 'Procedural Drama' },
  { id: 'world-3', icon: '🏔️', name: 'Northern Exposure Files', genre: 'Dramedy' },
  { id: 'world-4', icon: '🦸', name: 'Caped Crusader Archives', genre: 'Superhero' },
  { id: 'world-5', icon: '🌌', name: 'Deep Space Case Files', genre: 'Sci-Fi' },
  { id: 'world-6', icon: '🏰', name: 'Medieval Mystery Files', genre: 'Fantasy Drama' },
  { id: 'world-7', icon: '🎭', name: 'Stage & Screen Files', genre: 'Theater Comedy' },
];

interface VoteCounts {
  [worldId: string]: number;
}

interface PitchModalProps {
  onClose: () => void;
}

function PitchModal({ onClose }: PitchModalProps) {
  const [showName, setShowName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showName.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      const { error: insertError } = await supabase
        .from('fan_world_pitches')
        .insert({ show_name: showName.trim(), user_id: user?.id ?? null });
      if (insertError) throw insertError;
      setSubmitted(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.7)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-card border border-border rounded-2xl p-8 w-full max-w-md shadow-2xl">
        {submitted ? (
          <div className="text-center py-4">
            <div className="text-5xl mb-4">🎉</div>
            <h3 className="text-xl font-extrabold text-foreground mb-2">Pitch Submitted!</h3>
            <p className="text-muted-foreground mb-6">
              Thanks for pitching your Fan World idea. We&apos;ll review it and consider it for future expansion!
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl font-semibold text-sm"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff' }}
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-extrabold text-foreground">Pitch a Fan World</h3>
                <p className="text-sm text-muted-foreground mt-1">What show or movie should we build next?</p>
              </div>
              <button
                onClick={onClose}
                className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <label className="block text-sm font-semibold text-foreground mb-2">
                Show or Movie Name
              </label>
              <input
                type="text"
                value={showName}
                onChange={(e) => setShowName(e.target.value)}
                placeholder="e.g. Breaking Bad, The Office, Dune..."
                className="w-full bg-muted border border-border rounded-xl px-4 py-3 text-foreground placeholder:text-muted-foreground text-sm focus:outline-none focus:border-primary mb-4"
                maxLength={120}
                autoFocus
              />
              {error && (
                <p className="text-red-400 text-xs mb-3">{error}</p>
              )}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || !showName.trim()}
                  className="flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', color: '#fff' }}
                >
                  {submitting ? 'Submitting…' : 'Submit Pitch'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function IPExpansionSection() {
  const [voteCounts, setVoteCounts] = useState<VoteCounts>({});
  const [userVotes, setUserVotes] = useState<Set<string>>(new Set());
  const [votingWorldId, setVotingWorldId] = useState<string | null>(null);
  const [showPitchModal, setShowPitchModal] = useState(false);
  const [loadingVotes, setLoadingVotes] = useState(true);

  const fetchVotes = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('fan_world_votes')
        .select('world_id');
      if (error) throw error;

      const counts: VoteCounts = {};
      upcomingWorlds.forEach((w) => { counts[w.id] = 0; });
      (data || []).forEach((row: { world_id: string }) => {
        if (counts[row.world_id] !== undefined) counts[row.world_id]++;
        else counts[row.world_id] = 1;
      });
      setVoteCounts(counts);

      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: myVotes } = await supabase
          .from('fan_world_votes')
          .select('world_id')
          .eq('user_id', user.id);
        setUserVotes(new Set((myVotes || []).map((v: { world_id: string }) => v.world_id)));
      }
    } catch {
      // silently fail — votes are non-critical
    } finally {
      setLoadingVotes(false);
    }
  }, []);

  useEffect(() => {
    fetchVotes();
  }, [fetchVotes]);

  const handleVote = async (worldId: string) => {
    if (votingWorldId) return;
    setVotingWorldId(worldId);
    try {
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();

      if (userVotes.has(worldId)) {
        // Remove vote
        if (user) {
          await supabase
            .from('fan_world_votes')
            .delete()
            .eq('world_id', worldId)
            .eq('user_id', user.id);
        }
        setUserVotes((prev) => { const s = new Set(prev); s.delete(worldId); return s; });
        setVoteCounts((prev) => ({ ...prev, [worldId]: Math.max(0, (prev[worldId] || 1) - 1) }));
      } else {
        // Add vote
        const { error } = await supabase
          .from('fan_world_votes')
          .insert({ world_id: worldId, user_id: user?.id ?? null });
        if (!error) {
          setUserVotes((prev) => new Set(prev).add(worldId));
          setVoteCounts((prev) => ({ ...prev, [worldId]: (prev[worldId] || 0) + 1 }));
        }
      }
    } catch {
      // silently fail
    } finally {
      setVotingWorldId(null);
    }
  };

  return (
    <section id="expansion" className="py-24 px-6 max-w-screen-xl mx-auto">
      {showPitchModal && <PitchModal onClose={() => setShowPitchModal(false)} />}

      <div className="text-center mb-16">
        <p className="text-xs font-semibold uppercase tracking-widest text-secondary mb-3">IP Expansion Roadmap</p>
        <h2 className="text-hero-md font-extrabold text-foreground mb-4">
          More Worlds.<br />
          <span className="gradient-text-blue">More Fandoms. More Battles.</span>
        </h2>
        <p className="text-muted-foreground text-lg max-w-xl mx-auto">
          Psych is just the beginning. FanFinite is designed to host any IP universe.
          Vote on what comes next and be first in line when your fandom goes live.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {upcomingWorlds.map((world) => {
          const voted = userVotes.has(world.id);
          const count = voteCounts[world.id] ?? 0;
          const isVoting = votingWorldId === world.id;

          return (
            <button
              key={world.id}
              onClick={() => handleVote(world.id)}
              disabled={isVoting || loadingVotes}
              className={`bg-card border rounded-2xl p-5 transition-all duration-150 group text-left w-full cursor-pointer ${
                voted
                  ? 'border-primary/60 shadow-[0_0_12px_rgba(59,130,246,0.25)]'
                  : 'border-border hover:border-secondary/40'
              } disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <div className="flex items-start justify-between mb-3">
                <span className="text-3xl">{world.icon}</span>
                <div className="flex flex-col items-end gap-1">
                  {/* Vote count badge */}
                  <span
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex items-center gap-1 ${
                      voted
                        ? 'text-primary bg-primary/10 border-primary/30' :'text-muted-foreground bg-muted border-border'
                    }`}
                  >
                    {voted ? '✓ Voted' : '↑ Vote'}
                    <span className="font-bold">
                      {loadingVotes ? '…' : count.toLocaleString()}
                    </span>
                  </span>
                </div>
              </div>
              <h3 className="text-sm font-bold text-foreground mb-1">{world.name}</h3>
              <p className="text-xs text-muted-foreground mb-3">{world.genre}</p>
              <p className={`text-xs font-medium transition-colors ${voted ? 'text-primary' : 'text-secondary group-hover:text-primary'}`}>
                {voted ? 'Click to remove your vote' : 'Click to vote for this world'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Pitch a Fan World CTA */}
      <div className="flex justify-center mb-12">
        <button
          onClick={() => setShowPitchModal(true)}
          className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all duration-150 gradient-secondary hover:opacity-90 shadow-[0_0_16px_rgba(96,165,250,0.25)]"
        >
          <span className="text-lg">💡</span>
          Pitch a Fan World
        </button>
      </div>

      {/* Coming soon app download */}
      <div className="bg-card border border-border rounded-3xl p-10 text-center card-glow-blue">
        <div className="text-4xl mb-4">📱</div>
        <h3 className="text-2xl font-extrabold text-foreground mb-3">Mobile App Coming Soon</h3>
        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
          The FanFinite mobile experience is in development for iOS and Android.
          Sign up now to get early access when it launches.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <div className="flex items-center gap-3 bg-muted border border-border rounded-xl px-5 py-3 opacity-60 cursor-not-allowed">
            <span className="text-2xl">🍎</span>
            <div className="text-left">
              <p className="text-[10px] text-muted-foreground">Download on the</p>
              <p className="text-sm font-bold text-foreground">App Store</p>
            </div>
          </div>
          <div className="flex items-center gap-3 bg-muted border border-border rounded-xl px-5 py-3 opacity-60 cursor-not-allowed">
            <span className="text-2xl">🤖</span>
            <div className="text-left">
              <p className="text-[10px] text-muted-foreground">Get it on</p>
              <p className="text-sm font-bold text-foreground">Google Play</p>
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-4">Available Q4 2026 · Web version available now</p>
      </div>
    </section>
  );
}